import {
  ArrowRight,
  Heart,
  Users,
  Calendar,
  BookOpen,
  MessageCircle,
  Phone,
  Video,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import GriefAppointmentCalendar from "../../components/public/GriefAppointmentCalendar";

const SUPPORT_ITEMS = [
  {
    icon: Heart,
    body: "Preparing for an anticipated loss.",
  },
  {
    icon: MessageCircle,
    body: "Navigating grief after a death.",
  },
  {
    icon: Users,
    body: "Coping with loneliness and changes in daily life.",
  },
  {
    icon: Calendar,
    body: "Preparing for holidays, anniversaries, and meaningful dates.",
  },
  {
    icon: BookOpen,
    body: "Exploring questions of faith, meaning, and hope.",
  },
];

export default function IndividualsFamiliesPage() {
  usePageMeta({
    title:
      "Pastoral Grief Counseling with Chaplain Derrick Hall | California Corporate Chaplains",
    description:
      "Scheduled pastoral grief counseling with Chaplain Derrick Hall — supportive listening, grief education, and space to reflect after a loss. Sessions by phone or Zoom.",
    noindex: true,
  });

  return (
    <main className="bg-white" data-testid="individuals-families-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-10">
          <p
            className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3"
            data-testid="if-eyebrow"
          >
            Support for Individuals &amp; Families
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            data-testid="if-heading"
          >
            Pastoral Grief Counseling with Chaplain Derrick Hall
          </h1>
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-3xl text-lg">
            <p>
              Grief can leave you with questions, emotions, and changes that
              are difficult to carry alone. Whether you are preparing for a
              loss, grieving someone who has died, or adjusting to life
              afterward, compassionate support is available.
            </p>
            <p>
              Chaplain Derrick Hall provides a welcoming space to talk,
              reflect, and explore ways to move forward while honoring the
              people and relationships that matter to you.
            </p>
          </div>

          <div className="mt-8">
            <a
              href="#grief-appointment-calendar"
              data-testid="if-cta-primary"
              data-analytics-cta="grief-view-times"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors"
            >
              View Available Appointment Times <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* SOMEONE TO WALK ALONGSIDE YOU */}
        <section className="mt-4 md:mt-6" data-testid="if-walk-alongside">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            What sessions offer
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Someone to Walk Alongside You
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            As a hospice chaplain and bereavement counselor, Chaplain Hall
            supports individuals and families facing loss and life's difficult
            transitions. Sessions offer time for listening, meaningful
            conversation, grief education, and practical coping tools tailored
            to your needs.
          </p>
          <p className="mt-5 text-slate-700 leading-relaxed max-w-3xl">
            Support may include:
          </p>
          <ul
            className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4"
            data-testid="if-support-list"
          >
            {SUPPORT_ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                  data-testid={`if-support-item-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#F0F4F8] text-[#4A6FA5] shrink-0">
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {item.body}
                  </p>
                </li>
              );
            })}
          </ul>
          <p className="mt-6 text-slate-700 leading-relaxed max-w-3xl">
            People of all faiths and those with no religious affiliation are
            welcome. Spiritual conversation and prayer are included according
            to your preferences.
          </p>
        </section>

        {/* MEET BY PHONE OR ZOOM */}
        <section className="mt-16 md:mt-20" data-testid="if-phone-zoom">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            How we meet
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Meet by Phone or Zoom
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            Scheduled sessions are available by phone or Zoom, allowing you to
            connect from a comfortable, private location. Individual and family
            appointments can be discussed when requesting support.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Phone className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span className="text-sm font-medium text-slate-800">Phone</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Video className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span className="text-sm font-medium text-slate-800">Zoom</span>
            </div>
          </div>
        </section>

        {/* CALENDAR — appointment request */}
        <div id="grief-appointment-calendar" data-analytics-section="grief-appointment-calendar" className="scroll-mt-24">
          <GriefAppointmentCalendar />
        </div>

        {/* CLOSING */}
        <section className="mt-16 md:mt-20" data-testid="if-closing">
          <div className="rounded-3xl bg-gradient-to-br from-[#3B597B] via-[#4A6FA5] to-[#4A6FA5] p-8 sm:p-12 text-white">
            <p className="text-xs uppercase tracking-[0.22em] text-blue-100 font-semibold mb-3">
              Take a first step
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight max-w-2xl">
              Take the First Step
            </h2>
            <p className="mt-4 text-blue-100 leading-relaxed max-w-2xl">
              You do not need to have the right words or know where to begin.
              Reach out to discuss your needs and request an appointment with
              Chaplain Derrick Hall.
            </p>
            <div className="mt-8">
              <a
                href="#grief-appointment-calendar"
                data-testid="if-cta-closing"
                data-analytics-cta="grief-choose-time"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#3B597B] font-semibold hover:bg-blue-50"
              >
                Choose an Appointment Time <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* SCOPE NOTE */}
        <p
          className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl"
          data-testid="if-scope-note"
        >
          Services provide pastoral counseling, grief education, and emotional
          and spiritual support. They do not replace licensed mental health
          treatment or emergency care.
        </p>
      </div>
    </main>
  );
}
